
import { useCallback } from "react";
import { useQuery, QueryClientProvider } from "@tanstack/react-query";
import { message } from "antd";

import { BASE_PROJECTS_API_URL, QUERY_KEYS } from "../../config/constants";
import { apiFetch } from "../../utils/apiClient";
 
const extractProjects = (response) => {
  console.log("API Response:", response);
  const nested =
    response.projects && typeof response.projects === "object" && !Array.isArray(response.projects)
      ? (response.projects)
      : null;

  const content =
    nested?.projects ?? response.content ?? response.data ?? response.projects ?? [];
   console.log("Extracted content from API response:", content);
  return Array.isArray(content) ? (content) : [];
};

const extractPaginationMeta = (response, requestPage, requestSize) => {
  const toNumber = (value) => {
    if (typeof value === "number" && Number.isFinite(value)) return value;
    if (typeof value === "string" && value.trim() !== "") {
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : null;
    }
    return null;
  };

  const data =
    response?.data && typeof response.data === "object" ? response.data : null;

  const nested =
    response?.projects && typeof response.projects === "object" && !Array.isArray(response.projects)
      ? response.projects
      : null;

  const read = (...candidates) =>
    candidates.map((candidate) => toNumber(candidate)).find((value) => value !== null);

  const totalRecords =
    read(
      nested?.totalRecords,
      nested?.totalElements,
      nested?.total,
      nested?.totalCount,
      response?.totalRecords,
      response?.totalElements,
      response?.total,
      response?.totalCount,
      data?.totalRecords,
      data?.totalElements,
      data?.total,
      data?.totalCount,
    ) ?? 0;

  const currentPage =
    read(nested?.currentPage, nested?.page, response?.currentPage, response?.page, data?.currentPage, data?.page) ??
    requestPage;

  const pageSize =
    read(nested?.pageSize, nested?.size, response?.pageSize, response?.size, data?.pageSize, data?.size) ??
    requestSize;

  const totalPages =
    read(nested?.totalPages, response?.totalPages, data?.totalPages) ??
    (pageSize > 0 ? Math.ceil(totalRecords / pageSize) : 0);

  return {
    totalRecords,
    currentPage,
    pageSize,
    totalPages,
    hasNext: Boolean(nested?.hasNext ?? response?.hasNext ?? data?.hasNext),
    hasPrevious: Boolean(nested?.hasPrevious ?? response?.hasPrevious ?? data?.hasPrevious),
  };
};

export const useProjectList = (
  page = 0,
  size = 10,
  appliedFilters,
  sortBy = "projectStartDate",
  sortDirection = "asc",
) => {
  console.log("useProjectList called with:", { page, size, appliedFilters });
  const authScope = [
    sessionStorage.getItem("userId") || sessionStorage.getItem("email") || "",
    sessionStorage.getItem("role") || "",
    sessionStorage.getItem("token") ||
      sessionStorage.getItem("authToken") ||
      sessionStorage.getItem("accessToken") ||
      "",
  ].join("|");

  const filtersKey = JSON.stringify(appliedFilters ?? {});

  const fetchProjects = useCallback(async () => {
    const rawToken =
      sessionStorage.getItem("token") ||
      sessionStorage.getItem("authToken") ||
      sessionStorage.getItem("accessToken");

    const tokenType = sessionStorage.getItem("tokenType") || "Bearer";

    if (!rawToken) {
      throw new Error("Authentication token not found. Please log in again.");
    }

    const normalizedToken = rawToken
      .replace(/^"|"$/g, "")
      .replace(/^Bearer\s+/i, "")
      .trim();

    const requestUrl = new URL(BASE_PROJECTS_API_URL, globalThis.location.origin);

    requestUrl.searchParams.set("page", String(page));
    requestUrl.searchParams.set("size", String(size));
    requestUrl.searchParams.set("sortBy", sortBy || "projectStartDate");
    requestUrl.searchParams.set("sortDirection", sortDirection === "desc" ? "desc" : "asc");

    const filters = appliedFilters;

    const isValidFilterValue = (value) =>
      Boolean(value && value.trim() && value !== "all");

    if (isValidFilterValue(filters?.search)) {
      requestUrl.searchParams.set("search", filters.search.trim());
    }
    if (filters?.statusFilter === "true") {
      requestUrl.searchParams.set("status", "true");
    } else if (filters?.statusFilter === "false") {
      requestUrl.searchParams.set("status", "false");
    }
    if (isValidFilterValue(filters?.ragStatus)) {
      requestUrl.searchParams.set("ragStatus", filters.ragStatus);
    }

    if (isValidFilterValue(filters?.contractCategory)) {
      requestUrl.searchParams.set("contractCategory", filters.contractCategory);
    }

    const selectedBusinessTypes = [];

    if (Array.isArray(filters?.businessType)) {
      selectedBusinessTypes.push(...filters.businessType);
    } else if (isValidFilterValue(filters?.businessType)) {
      selectedBusinessTypes.push(filters.businessType);
    }

    if (selectedBusinessTypes.length) {
      selectedBusinessTypes
        .map((businessType) => businessType.trim())
        .filter(Boolean)
        .forEach((businessType) => {
          requestUrl.searchParams.append("businessType", businessType);
        });
    }

    if (isValidFilterValue(filters?.clientPartner)) {
      requestUrl.searchParams.set("clientPartner", filters.clientPartner);
    }

    if (isValidFilterValue(filters?.deliveryManager)) {
      requestUrl.searchParams.set("deliveryManager", filters.deliveryManager);
    }

    if (isValidFilterValue(filters?.projectManager)) {
      requestUrl.searchParams.set("projectManager", filters.projectManager);
    }

    if (filters?.dateRange && filters?.dateRangeType) {
      const [fromRaw, toRaw] = filters.dateRange.split("|");

      switch (filters.dateRangeType) {
        case "DATE": {
          const toIsoDate = (value) => {
            if (!value) return "";

            const [month, day, year] = value.split("-");

            if (!month || !day || !year) {
              return "";
            }

            return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
          };

          const fromDate = toIsoDate(fromRaw);
          const toDate = toIsoDate(toRaw);

          if (fromDate) {
            requestUrl.searchParams.set("fromDate", fromDate);
          }

          if (toDate) {
            requestUrl.searchParams.set("toDate", toDate);
          }

          break;
        }

        case "MONTH": {
          const monthMap = {
            Jan: "01",
            Feb: "02",
            Mar: "03",
            Apr: "04",
            May: "05",
            Jun: "06",
            Jul: "07",
            Aug: "08",
            Sep: "09",
            Oct: "10",
            Nov: "11",
            Dec: "12",
          };

          const toMonthRange = (value, isEnd = false) => {
            if (!value) return "";

            const [monthText, year] = value.split(" ");

            if (!monthText || !year) {
              return "";
            }

            const month = monthMap[monthText];

            if (!month) {
              return "";
            }

            if (!isEnd) {
              return `${year}-${month}-01`;
            }

            const lastDay = new Date(Number(year), Number(month), 0).getDate();

            return `${year}-${month}-${String(lastDay).padStart(2, "0")}`;
          };

          const fromDate = toMonthRange(fromRaw);
          const toDate = toMonthRange(toRaw, true);

          if (fromDate) {
            requestUrl.searchParams.set("fromDate", fromDate);
          }

          if (toDate) {
            requestUrl.searchParams.set("toDate", toDate);
          }

          break;
        }

        case "YEAR": {
          const fromYear = fromRaw?.trim();
          const toYear = toRaw?.trim();

          if (fromYear) {
            requestUrl.searchParams.set("fromDate", `${fromYear}-01-01`);
          }

          if (toYear) {
            requestUrl.searchParams.set("toDate", `${toYear}-12-31`);
          }

          break;
        }

        default:
          break;
      }
    }

    const response = await apiFetch(requestUrl.toString(), {
      method: "GET",
      headers: {
        Authorization: `${tokenType} ${normalizedToken}`,
        Accept: "application/json",
      },
    }, "Unable to load projects right now. Please try again later.", { passthrough: [401, 403] });
    console.log("Request URL:", requestUrl.toString());
    console.log("Response Status:", response);
    const contentType = response.headers.get("content-type") || "";
    let responseBody;

    if (contentType.includes("application/json")) {
      responseBody = (await response.json());
      console.log("Response Body:", responseBody);
    } else {
      const rawText = await response.text();

      try {
        responseBody = JSON.parse(rawText);
      } catch {
        responseBody = { message: rawText };
      }
    }

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error("Unauthorized. Please log in again.");
      }

      if (response.status === 403) {
        throw new Error("You don't have permission to view projects.");
      }

      console.error("Project list API error:", response.status, responseBody);
      throw new Error("Unable to load projects right now. Please try again later.");
    }
    console.log("Response Body after parsing:", responseBody);
    
    const apiProjects = extractProjects(responseBody);
    const pagination = extractPaginationMeta(responseBody, page, size);

    const rawProjects = apiProjects.map((project, index) => ({
      ...project,
      id: String(project.id ?? project.projectId ?? `${page}-${size}-${index}`),
    }));
     console.log("apiProjects:", apiProjects);
    const projects = apiProjects
      .map((project, index) => ({
        id: String(project.projectId),
        projectId: String(project.projectId),
        projectName: String(
          project.projectDescription ??
            project.projectName ??
            project.projectTitle ??
            project.title ??
            project.description ??
            "-",
        ),
        customerName: String(project.customerName ?? project.client ?? project.accountName ?? "-"),
        managerName: String(project.managerName ?? project.pmName ?? project.projectManager ?? "-"),
        currencyType: project.currencyType,
        contractCategory: String(project.contractCategory ?? project.category ?? "-"),
        startDate: project.projectStartDate,
        endDate: project.projectEndDate ,
        status: project.status,
        redFlagged: Boolean(project.redFlagged),
        hasSowData: null,
      }))
      .filter((project) => project.projectId !== "-" || project.projectName !== "-");
     console.log("Fetched Projects:", projects);
    
    return { projects, rawProjects, pagination };
  }, [page, size, appliedFilters, sortBy, sortDirection]);

  const { data, isLoading, isFetching, error, refetch } = useQuery({
    queryKey: [QUERY_KEYS.projects, authScope, page, size, filtersKey, sortBy, sortDirection],
    queryFn: fetchProjects,
    staleTime: 0,
    refetchOnMount: "always",
    retry: 1,
    meta: {
      onError: (error) => {
        message.error(error.message);
        console.error(error);
      },
    },
  });

  return {
    projects: data?.projects ?? [],
    rawProjects: data?.rawProjects ?? [],
    pagination: data?.pagination ?? null,
    isLoading,
    isFetching,
    error,
    refetch,
  };
};
